import type { Hue } from '../ui/v2/hue';

/**
 * One colour per reason for contacting the store, shared by Calls, Texts,
 * Voicemail and the dashboard. The backend's primary intents and the
 * free-text labels pages show both resolve through `intentHue`, so
 * "Reservation", "New Reservation" and `RESERVATION` are the same colour.
 */
export const INTENT_HUE = {
  RESERVATION: 'accent',
  ORDER: 'teal',
  STORE_INFO: 'amber',
  ISSUE: 'rose',
  EVENT: 'violet',
  CATERING: 'violet',
  VOICEMAIL: 'sky',
  OTHER: 'lime',
} as const satisfies Record<string, Hue>;

export type IntentKey = keyof typeof INTENT_HUE;

const RULES: [RegExp, IntentKey][] = [
  [/reserv|table|booking/i, 'RESERVATION'],
  [/order|pickup|delivery|menu/i, 'ORDER'],
  [/cater/i, 'CATERING'],
  [/event|party|private|birthday/i, 'EVENT'],
  [/hour|store|info|direction|park|location|address/i, 'STORE_INFO'],
  [/issue|complain|lost|found|human|manager|refund|problem/i, 'ISSUE'],
  [/voicemail|message/i, 'VOICEMAIL'],
];

export function intentKey(label: string | null | undefined): IntentKey {
  if (!label) return 'OTHER';
  const upper = label
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (upper in INTENT_HUE) return upper as IntentKey;
  // EVENT_BOOKING is a backend primary intent. The booking rule would
  // otherwise claim it before event does, and paint it reservation blue.
  if (upper.startsWith('EVENT_')) return 'EVENT';
  for (const [re, key] of RULES) if (re.test(label)) return key;
  return 'OTHER';
}

export function intentHue(label: string | null | undefined): Hue {
  return INTENT_HUE[intentKey(label)];
}

/** Short labels for the call list. Keys are CallFact primary intents. */
export const INTENT_LABEL: Record<string, string> = {
  ORDER: 'Orders',
  RESERVATION: 'Reservations',
  EVENT_BOOKING: 'Events',
  CATERING: 'Catering',
  STORE_INFO: 'Store info',
  ISSUE: 'Issues',
  OTHER: 'Other',
};

export function intentLabel(code: string | null | undefined): string | null {
  if (!code?.trim()) return null;
  const key = code.trim().toUpperCase();
  return INTENT_LABEL[key] ?? null;
}
